import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PIPELINE_STAGES, PARTICIPANT_GOAL } from "@/lib/constants";
import { type PipelineStage } from "@/lib/types";

interface PipelineMetricsProps {
  stageStats: Record<PipelineStage, number>;
  totalProspects: number;
}

export function PipelineMetrics({ stageStats, totalProspects }: PipelineMetricsProps) {
  const participantCount = stageStats.participant || 0;
  const committedCount = stageStats.committed || 0;
  
  // Calculate conversion rate from new to participant
  const conversionRate = totalProspects > 0 
    ? Math.round((participantCount / totalProspects) * 100) 
    : 0;

  return (
    <>
      {/* Top metric cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Prospects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProspects}</div>
            <p className="text-xs text-muted-foreground">All pipeline stages</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Participants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{participantCount}</div>
            <p className="text-xs text-muted-foreground">Goal: {PARTICIPANT_GOAL}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Committed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{committedCount}</div>
            <p className="text-xs text-muted-foreground">Ready to convert</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conversionRate}%</div>
            <p className="text-xs text-muted-foreground">New → Participant</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline health breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Pipeline Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {PIPELINE_STAGES.map(({ value, label }) => {
              const count = stageStats[value] || 0;
              const percentage = totalProspects > 0 
                ? Math.round((count / totalProspects) * 100) 
                : 0;
              
              return (
                <div key={value} className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-xs text-muted-foreground">({percentage}%)</span>
                  </div>
                  <Badge 
                    variant={value === 'participant' ? 'default' : 'secondary'}
                    className="font-mono"
                  >
                    {count}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}